import { createClient } from '@supabase/supabase-js';

const DEV_FALLBACK_URL = 'https://bmbjoleclnfrpxpxcazl.supabase.co';
const DEV_FALLBACK_ANON =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJtYmpvbGVjbG5mcnB4cHhjYXpsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjQzNjA4MzgsImV4cCI6MjA3OTkzNjgzOH0.Q0lgbqUixuq8YhwBjQLMvcMavD-n4IV_cRHnInVA4gg';

// NOTE: anon key is public by design for client apps; this fallback prevents
// hard-crash white screens if deployment env vars are missing.
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || DEV_FALLBACK_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || DEV_FALLBACK_ANON;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    flowType: 'pkce',
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'ttt-supabase-auth',
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
});

// User registration - stores user info in Supabase
export async function registerUser({ email, firstName, lastName }) {
  const { data, error } = await supabase
    .from('users')
    .insert([
      {
        email: email.toLowerCase().trim(),
        first_name: firstName.trim(),
        last_name: lastName?.trim() || null,
        created_at: new Date().toISOString(),
      }
    ])
    .select()
    .single();

  if (error) {
    // Check if user already exists
    if (error.code === '23505') {
      // User exists, fetch their data
      const { data: existingUser, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email.toLowerCase().trim())
        .single();
      
      if (fetchError) throw fetchError;
      return { user: existingUser, isExisting: true };
    }
    throw error;
  }

  return { user: data, isExisting: false };
}

// Check if user is already registered by email
export async function getUserByEmail(email) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('email', email.toLowerCase().trim())
    .single();

  if (error && error.code !== 'PGRST116') {
    throw error;
  }

  return data;
}

// Submit feedback/bug report to Supabase
export async function submitFeedback({ type, message, language, userEmail = null }) {
  const { error } = await supabase
    .from('feedback')
    .insert({
      type: type, // 'bug' or 'comment'
      message: message.trim(),
      language: language,
      user_email: userEmail,
    });

  if (error) {
    console.error('Supabase feedback error:', error);
    throw error;
  }

  return { success: true };
}

// ========== CONVERSATION HISTORY FUNCTIONS ==========

/** True if string looks like a UUID (Supabase user_id column). */
function isUuid(value) {
  if (value == null) return false;
  const s = String(value);
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/**
 * Dev / local users use ids like dev-user-123, dev-dummy-456, not UUIDs.
 * Resolve to public.users.id via email (lookup or register placeholder row).
 */
async function resolveUserIdForConversation(userId, userEmail) {
  if (isUuid(userId)) {
    return String(userId);
  }
  if (!userEmail || typeof userEmail !== 'string') {
    console.warn('[Supabase] Cannot map non-UUID user without email:', userId);
    return null;
  }
  const email = userEmail.toLowerCase().trim();
  console.log('[Supabase] Resolving dev/local user id via email:', { userId, email });
  try {
    let userData = await getUserByEmail(email);
    if (!userData) {
      const { user: newUser } = await registerUser({
        email,
        firstName: 'Developer',
        lastName: 'Access',
      });
      userData = newUser;
      console.log('[Supabase] Created placeholder users row for dev/local:', userData?.id);
    }
    return userData?.id && isUuid(userData.id) ? userData.id : null;
  } catch (err) {
    console.error('[Supabase] resolveUserIdForConversation failed:', err);
    return null;
  }
}

// Create a new conversation
export async function createConversation({ userId, userEmail, situationType, situationId, metadata = {} }) {
  console.log('createConversation called with:', { userId, userEmail, situationType, situationId });

  const actualUserId = await resolveUserIdForConversation(userId, userEmail);
  if (!actualUserId) {
    console.warn('[Supabase] Skipping conversation create, no valid user_id (dev user without DB mapping?)');
    return null;
  }

  console.log('Inserting conversation with user_id:', actualUserId);
  const { data, error } = await supabase
    .from('conversations')
    .insert({
      user_id: actualUserId,
      situation_type: situationType,
      situation_id: situationId,
      metadata: metadata,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) {
    console.error('Supabase create conversation error:', error);
    throw error;
  }

  console.log('Conversation created successfully:', data);
  return data;
}

// Save a message to a conversation
export async function saveMessage({ conversationId, role, text, suggestions = [], metadata = {}, sequenceNumber }) {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      conversation_id: conversationId,
      role: role,
      text: text,
      suggestions: suggestions,
      metadata: metadata,
      sequence_number: sequenceNumber,
      created_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) {
    console.error('Supabase save message error:', error);
    throw error;
  }

  // Update conversation's updated_at timestamp
  await supabase
    .from('conversations')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', conversationId);

  return data;
}

// Get all conversations for a user (by user ID or email for dev users)
export async function getUserConversations(userIdOrEmail) {
  console.log('getUserConversations called with:', userIdOrEmail);
  
  let actualUserId = userIdOrEmail;

  // If it's an email (for dev users), find user_id from users table first
  if (userIdOrEmail && userIdOrEmail.includes('@')) {
    console.log('Looking up user by email:', userIdOrEmail);
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('email', userIdOrEmail.toLowerCase().trim())
      .single();
    
    if (userError) {
      console.log('User lookup error:', userError);
    }
    
    if (userError || !userData) {
      console.log('User not found in Supabase for email:', userIdOrEmail);
      return [];
    }
    
    actualUserId = userData.id;
    console.log('Found user ID:', actualUserId);
  }

  console.log('Querying conversations for user_id:', actualUserId);
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('user_id', actualUserId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Supabase get conversations error:', error);
    throw error;
  }

  console.log('Found conversations:', data?.length || 0);
  return data || [];
}

// Get all messages for a conversation
export async function getConversationMessages(conversationId) {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('sequence_number', { ascending: true });

  if (error) {
    console.error('Supabase get messages error:', error);
    throw error;
  }

  return data || [];
}

// Get full conversation with messages
export async function getConversationWithMessages(conversationId) {
  const { data: conversation, error: convError } = await supabase
    .from('conversations')
    .select('*')
    .eq('id', conversationId)
    .single();

  if (convError) {
    console.error('Supabase get conversation error:', convError);
    throw convError;
  }

  const messages = await getConversationMessages(conversationId);

  return {
    ...conversation,
    messages: messages,
  };
}

// Delete a conversation (and its messages via CASCADE)
export async function deleteConversation(conversationId) {
  const { error } = await supabase
    .from('conversations')
    .delete()
    .eq('id', conversationId);

  if (error) {
    console.error('Supabase delete conversation error:', error);
    throw error;
  }

  return { success: true };
}

// Update conversation metadata (e.g., title, inferredPlace, etc.)
export async function updateConversation(conversationId, updates) {
  const { data, error } = await supabase
    .from('conversations')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    console.error('Supabase update conversation error:', error);
    throw error;
  }

  return data;
}

// ========== NEWSLETTER FUNCTIONS ==========

// Subscribe to newsletter - stores email in Supabase
export async function subscribeToNewsletter({ email, language = 'en' }) {
  const { data, error } = await supabase
    .from('newsletter_subscribers')
    .insert([
      {
        email: email.toLowerCase().trim(),
        language: language,
        subscribed_at: new Date().toISOString(),
      }
    ])
    .select()
    .single();

  if (error) {
    // Check if email already exists
    if (error.code === '23505') {
      // Email already subscribed, return success
      const { data: existingSubscriber } = await supabase
        .from('newsletter_subscribers')
        .select('*')
        .eq('email', email.toLowerCase().trim())
        .single();
      
      return { subscriber: existingSubscriber, isExisting: true };
    }
    throw error;
  }

  return { subscriber: data, isExisting: false };
}

// ========== ACCESS REQUEST FUNCTIONS ==========

// Request website access - stores access request in Supabase
export async function requestAccess({ email, name, reason, language = 'en' }) {
  const { error } = await supabase
    .from('access_requests')
    .insert([
      {
        email: email.toLowerCase().trim(),
        name: name?.trim() || null,
        reason: reason?.trim() || null,
        language: language,
        requested_at: new Date().toISOString(),
        status: 'pending',
      }
    ]);

  if (error) {
    console.error('Supabase access request error:', error);
    throw error;
  }

  return { success: true };
}

// ========== IDIOMS FUNCTIONS ==========

// Get all idioms (optionally filtered by language and/or category)
export async function getIdioms({ language = null, category = null } = {}) {
  let query = supabase
    .from('idioms')
    .select('*')
    .order('created_at', { ascending: false });

  if (language) {
    query = query.eq('language', language);
  }

  if (category) {
    query = query.eq('category', category);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Supabase get idioms error:', error);
    throw error;
  }

  return data || [];
}

// Search idioms by phrase (case-insensitive partial match)
export async function searchIdioms(searchText, { language = null } = {}) {
  let query = supabase
    .from('idioms')
    .select('*')
    .ilike('phrase', `%${searchText}%`)
    .order('created_at', { ascending: false });

  if (language) {
    query = query.eq('language', language);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Supabase search idioms error:', error);
    throw error;
  }

  return data || [];
}

// Get a single idiom by ID
export async function getIdiomById(id) {
  const { data, error } = await supabase
    .from('idioms')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Supabase get idiom error:', error);
    throw error;
  }

  return data;
}

// Add a new idiom (requires authentication)
export async function addIdiom({ language, phrase, literal_meaning, explanation, category }) {
  const { data, error } = await supabase
    .from('idioms')
    .insert({
      language,
      phrase,
      literal_meaning,
      explanation,
      category,
    })
    .select()
    .single();

  if (error) {
    console.error('Supabase add idiom error:', error);
    throw error;
  }

  return data;
}

// ========== SUBSCRIPTION / PLAN STATUS ==========

// Legacy functions for backwards compatibility
// These are now replaced by Supabase Auth + subscriptions table

// Persist a premium flag on the user's access_requests row so it survives localStorage clears.
// NOTE: subscription_status column doesn't exist - using localStorage instead
// DEPRECATED: Use createOrUpdateSubscription instead
export async function markUserAsPremium(email) {
  if (!email) return;
  // Plan status is persisted via localStorage in Success.jsx
  // No need to store in Supabase as the app uses localStorage as the source of truth
  console.log('[TTT] Premium status marked in localStorage for:', email);
}

// Read plan status for a given email. Returns 'premium', 'free', or null.
// NOTE: Using localStorage as source of truth instead of Supabase
// DEPRECATED: Use useSubscription hook instead
export async function getUserPlanStatus(email) {
  if (!email) return null;
  // Check localStorage for plan status
  const planStatus = localStorage.getItem('ttt_has_plan');
  return planStatus || null;
}

// ========== NEW SUBSCRIPTION MANAGEMENT ==========

// Create or update subscription record in Supabase
export async function createOrUpdateSubscription({ 
  userEmail, 
  stripeCustomerId = null, 
  stripeSubscriptionId = null,
  status = 'active',
  currentPeriodEnd = null 
}) {
  if (!userEmail) {
    throw new Error('User email is required');
  }

  // Default to 30 days from now if not provided
  const periodEnd = currentPeriodEnd || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('subscriptions')
    .upsert({
      user_email: userEmail.toLowerCase().trim(),
      stripe_customer_id: stripeCustomerId,
      stripe_subscription_id: stripeSubscriptionId,
      status: status,
      current_period_end: periodEnd,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'stripe_subscription_id',
      ignoreDuplicates: false
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating/updating subscription:', error);
    throw error;
  }

  return data;
}

// Get subscription by user email
export async function getSubscriptionByEmail(email) {
  if (!email) return null;

  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_email', email.toLowerCase().trim())
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching subscription:', error);
    throw error;
  }

  return data;
}

// Check if user has active subscription
export async function checkActiveSubscription(email) {
  if (!email) return false;

  const subscription = await getSubscriptionByEmail(email);
  
  if (!subscription) return false;

  // Check for all valid active subscription statuses
  const validStatuses = ['active', 'trialing', 'subscribed'];
  const isActive = validStatuses.includes(subscription.status);
  const notExpired = new Date(subscription.current_period_end) > new Date();

  return isActive && notExpired;
}

// Cancel subscription (marks as canceled, doesn't delete)
export async function cancelSubscription(stripeSubscriptionId) {
  if (!stripeSubscriptionId) {
    throw new Error('Stripe subscription ID is required');
  }

  const { data, error } = await supabase
    .from('subscriptions')
    .update({ 
      status: 'canceled',
      updated_at: new Date().toISOString()
    })
    .eq('stripe_subscription_id', stripeSubscriptionId)
    .select()
    .single();

  if (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }

  return data;
}

// ========== ACCESS REQUESTS - POST CODE ENTRY ==========

// Check if email already exists in access_requests
export async function checkEmailExists(email) {
  const { data, error } = await supabase
    .from('access_requests')
    .select('id, email, status')
    .eq('email', email.toLowerCase().trim())
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Supabase check email error:', error);
    throw error;
  }

  return data || null;
}

// Save user info after code entry
// Inserts with 'pending' status - will be reviewed and approved
// Supabase trigger automatically sends webhook notification
export async function saveUserInfo({ email, name, language = 'en' }) {
  const { data, error } = await supabase
    .from('access_requests')
    .insert([
      {
        email: email.toLowerCase().trim(),
        name: name.trim(),
        status: 'pending',  // User requests access, admin reviews
        requested_at: new Date().toISOString(),
        language: language,
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Supabase save user info error:', error);
    throw error;
  }

  return data;
}